import { Beacon, TBeaconEntry } from '@ixicare/resources';
import { Button } from '@ixicare/ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericTable } from '../GenericTable';
import { useOnReachBottom } from '../utils';
import SiteStore from '~modules/sites/store/Site';
import { TransformContext } from '../rowTransformers';
import { useTableRowActions } from '../hooks/useTableRowActions';
import { mapDBGWithType } from '~modules/devices/utils/deviceMapping';
import { useDeviceDetail } from '~modules/sites/hooks/useDeviceDetail';
import { deleteAction, editAction, navigateMapAction } from '../deviceTableActions';
import { useDeviceArchive } from '~modules/sites/hooks/useDeviceArchive';
import { useStores } from '~store/utils';
import { useSortableQuery } from '../hooks/useSortableQuery';
import { BaseTableConfig, WithTransformer, WithColumnManager, WithExport } from '../types';
import { DeleteDeviceModal } from '~modules/devices/components/DeviceModals/DeleteDeviceModal';
import { UpdateDeviceModal } from '~modules/devices/components/DeviceModals/UpdateDeviceModal';

interface BeaconsTableProps {
  site: SiteStore | null;
  isArchived: boolean;
  config: BaseTableConfig & WithTransformer<TBeaconEntry> & WithColumnManager<TBeaconEntry> & WithExport<TBeaconEntry>;
  selectedKeys?: Set<string>;
  onSelectedKeysChange?: (next: Set<string>) => void;
}

export function BeaconsTable({ site, isArchived, config, selectedKeys, onSelectedKeysChange }: BeaconsTableProps) {
  const { notifications } = useStores();
  const navigate = useNavigate();

  const queryFn = ({ page, perPage, orderBy, order }) =>
    Beacon.getBeacons({
      siteId: site?.uuid,
      page,
      perPage,
      orderBy,
      order,
      isArchived,
    });

  const {
    sortedData,
    orderBy,
    ascOrder,
    mode,
    handleSort,
    refetch,
    isRefreshing,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    loadAll,
  } = useSortableQuery<TBeaconEntry>({
    queryKey: config.tableId,
    queryFn,
    scope: [site?.uuid, isArchived],
    enabled: !!site?.uuid,
    defaultOrderBy: config.defaultOrderBy ?? 'id',
    defaultAscOrder: config.defaultAscOrder,
    sortAccessors: config.sortAccessors,
  });

  useOnReachBottom(fetchNextPage, !!site?.uuid && mode === 'paginated' && !isRefreshing);

  const context: TransformContext = {
    isArchived,
    siteId: site?.id,
    showArchiveColumn: false,
    showTypeColumn: false,
  };

  const rows = React.useMemo(() => {
    return sortedData.map((x) => config.transformRow(x, context));
  }, [sortedData, isArchived, site?.id]);

  const devicesWithType = React.useMemo(() => {
    return mapDBGWithType(sortedData);
  }, [sortedData]);

  const { selectedDevices, setSelectedDevices, selectedDeviceDetail, handleCloseDetail } =
    useDeviceDetail(devicesWithType);

  const { devicesToArchive, setDevicesToArchive, deleteMutation } = useDeviceArchive({
    onRefresh: refetch,
    notifications,
  });

  const { actionProps, onActionClick, isActionVisible } = useTableRowActions(
    devicesWithType,
    isArchived
      ? []
      : [
          navigateMapAction(navigate, site?.uuid, isArchived, (dbg) => dbg.type),
          editAction((dbgs) => setSelectedDevices(dbgs)),
          deleteAction((dbgs) => setDevicesToArchive(dbgs)),
        ]
  );

  const showLoadMore = mode === 'paginated' && hasNextPage && !error;

  return (
    <>
      <GenericTable
        config={config}
        orderBy={orderBy}
        asc={ascOrder}
        rows={rows ?? []}
        onClickColumn={handleSort}
        actions={actionProps}
        onActionClick={onActionClick}
        isActionVisible={isActionVisible}
        getRowLabel={(i) => devicesWithType[i]?.macAddress}
        getRowKey={(i) => String(devicesWithType[i]?.id ?? i)}
        selectedKeys={selectedKeys}
        onSelectedKeysChange={onSelectedKeysChange}
        onRefresh={refetch}
        isRefreshing={isRefreshing}
        csvData={sortedData}
        csvAccessors={{ ...config.sortAccessors, ...config.csvAccessors }}
        onPrepareExport={loadAll}
      />
      {showLoadMore && (
        <Button loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Load more
        </Button>
      )}
      {selectedDevices && selectedDeviceDetail && (
        <UpdateDeviceModal
          type={selectedDeviceDetail.type}
          devices={selectedDevices}
          onClose={handleCloseDetail}
          onRefresh={refetch}
        />
      )}
      {devicesToArchive.length > 0 && (
        <DeleteDeviceModal
          devices={devicesToArchive}
          onClose={() => setDevicesToArchive([])}
          onDelete={() => deleteMutation.mutate()}
          isPending={deleteMutation.isPending}
        />
      )}
    </>
  );
}
